/* eslint-disable @typescript-eslint/no-explicit-any */
import {useEffect, useState} from 'react';
import {JOBFEED, SETTINGS} from '@constants';
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import dynamic from 'next/dynamic';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {VersionVariants} from '@jobseeker/lotus/enums';
import NavigationPanel from '@jobseeker/lotus/components/NavigationPanel';
import NavigationPanelHirist from '@jobseeker/lotus/components/NavigationPanelHirist';
import {
  BackButtonHeader,
  BannerInfo,
  HiristBanner,
} from '@jobseeker/lotus/components';
import {Box, Grid, Typography} from '@mui/material';
import {useRouter} from 'next/router';
import {useIsCustomWidth, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {wrapper} from '@jobseeker/common-core/stores';
import {utilServerSideDeviceDetection} from '@utils';
import {getUserSettings} from '@jobseeker/common-core/reducers/user/thunkServices';
import type {GetServerSidePropsContext} from 'next/types';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import cookie from 'cookie';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import {getCookiesToDelete, setSSRCookies} from '@helpers/ssrMap';
import {userService} from '@jobseeker/common-core/services';
import profileService from '@jobseeker/common-core/services/profile.service';
import {useValidateAuth} from '@jobseeker/common-core';
import {loadSEO} from '@helpers';
import {userProfile} from '@jobseeker/common-core/helpers';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import InjectSearchOverlay from '@components/SearchInjection';
import type {AppState} from '@jobseeker/common-core/types';
import Button from '@jobseeker/lotus/elements/Button';
import {Settings} from '@jobseeker/lotus/assets/icons';

const NotificationComponent = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/NotificationSetting'
    )
);

const PrivacyComponent = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/PrivacySetting'
    )
);

const SecurityComponent = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/SecuritySetting'
    )
);

const OtherComponent = dynamic(
  () =>
    import('@jobseeker/feature-profile/components/ProfileSetting/OtherSetting')
);

const AccountComponent = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/AccountSetting'
    )
);

const VerifyContactComponent = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/VerifyContactSetting'
    )
);

const HiristNotificationSetting = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/Hirist/Notifications'
    )
);

const HiristPrivacySetting = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/Hirist/Privacy'
    )
);

const HiristOtherSetting = dynamic(
  () =>
    import('@jobseeker/feature-profile/components/ProfileSetting/Hirist/Others')
);

const HiristAccountComponent = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/Hirist/Account'
    )
);

const HiristChangePassword = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/Hirist/ChangePassword'
    )
);

const HiristVerifyContact = dynamic(
  () =>
    import(
      '@jobseeker/feature-profile/components/ProfileSetting/Hirist/VerifyContactDetails'
    )
);

type SettingProps = {
  seoConfig: object;
  isMobile: boolean;
  activeSection: string;
};

type ComponentMap = {
  [key: string]: JSX.Element;
};

interface MobileNavbarProps {
  menuItems: {label: string; key: string}[];
  selectedKey: string | undefined;
  onSelect: (key: string) => void;
}

const MobileNavbar = ({
  menuItems,
  selectedKey,
  onSelect,
}: MobileNavbarProps) => {
  const theme = useWrappedTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        width: '100%',
        backgroundColor: 'background.paper',
        boxShadow: 1,
        padding: '12px',
      }}
    >
      {menuItems.map(item => (
        <Button
          key={item.key}
          variant={'text'}
          onClick={() => onSelect(item.key)}
          sx={{
            color:
              selectedKey === item.key
                ? theme.palette.primary.main
                : 'text.primary',
            flexShrink: 0,
            py: 1,
            px: 1,
            marginRight: '8px',
            borderRadius: '20px',
            whiteSpace: 'nowrap',
            position: 'relative',
            '&::after': {
              content: '""',
              display: 'block',
              width: '30%',
              height: '3px',
              backgroundColor:
                selectedKey === item.key
                  ? theme.palette.primary.main
                  : 'transparent',
              position: 'absolute',
              bottom: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              transition: 'background-color 0.3s ease-in-out',
            },
          }}
        >
          <Typography variant="body2">{item.label}</Typography>
        </Button>
      ))}
    </Box>
  );
};

const MySettings = ({seoConfig, activeSection, isMobile}: SettingProps) => {
  const hirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';
  const router = useRouter();
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, 'userSetings');
  const isSmall = useIsCustomWidth(900);
  const breadcrumbElements = hirist
    ? []
    : [
        {name: 'Home', item: '/'},
        {name: 'My Settings', item: '/settings'},
      ];

  const [selectedItem, setSelectedItem] = useState<string | undefined>(
    activeSection
  );
  const [showNavPanel, setShowNavPanel] = useState<boolean>(true);
  const dispatch: any = useDispatch();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [registerBy, setRegisterBy] = useState<number>(0);
  const profile = useSelector((state: AppState) => state.user.profile);
  useValidateAuth(dispatch, true);

  const getSettings = () => {
    dispatch(getUserSettings());
  };

  const getProfile = async () => {
    const [, aiData] = await Promise.all([
      userProfile('/profile', dispatch),
      profileService.getProfile({ai: true}),
    ]);
    setRegisterBy(aiData?.ai?.registerBy || 0);
    setLoading(false);
  };

  useEffect(() => {
    getSettings();
    getProfile();
  }, []);

  useEffect(() => {
    if (profile && Object.keys(profile).includes('invalidEmail')) {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    if (hirist) {
      if (!selectedItem) {
        router.replace(
          {
            pathname: router.pathname,
            query: {section: 'emailnotification'},
          },
          undefined,
          {shallow: true}
        );
        setSelectedItem('emailnotification');
      } else if (selectedItem && window.innerWidth < 900) {
        setShowNavPanel(false);
      }
    } else {
      if (window.innerWidth > 900 && !selectedItem) {
        router.replace(
          {
            pathname: router.pathname,
            query: {section: 'notifications'},
          },
          undefined,
          {shallow: true}
        );
        setSelectedItem('notifications');
      } else if (selectedItem && window.innerWidth < 900) {
        setShowNavPanel(false);
      }
    }
  }, [selectedItem, isMobile]);

  useEffect(() => {
    const section = Array.isArray(router?.query?.section)
      ? router.query.section[0]
      : router?.query?.section;

    setSelectedItem(section ? section.toLowerCase() : undefined);

    if (isSmall) {
      setShowNavPanel(false);
    }
    if (!section) {
      setShowNavPanel(true);
    }

    // Skip the transient no-section render: it's immediately replaced by a
    // default ?section= redirect (hirist always, or desktop for iimjobs),
    // which re-fires this effect with the real section - firing here too
    // would double-count the page view.
    const willRedirectToDefaultSection = !section && (hirist || !isMobile);
    if (!willRedirectToDefaultSection) {
      TrackEventUBA('settingsPageView', 'view', {
        ref: router.query.ref || '',
        pageName: 'Setting Page',
        pageSection: section,
        sessionUsername: `${sessionStorage.getItem('userId')}`,
      });
    }
  }, [router?.query?.section]);

  const onSelect = (item: string | number) => {
    router.push(
      {
        pathname: router.pathname,
        query: {section: item},
      },
      undefined,
      {shallow: true}
    );
  };

  const componentMap: ComponentMap = hirist
    ? {
        emailnotification: <HiristNotificationSetting loading={isLoading} />,
        privacy: <HiristPrivacySetting loading={isLoading} />,
        account: <HiristAccountComponent loading={isLoading} />,
        changepassword: <HiristChangePassword loading={isLoading} />,
        'verify-contact-details': <HiristVerifyContact loading={isLoading} />,
        other: <HiristOtherSetting loading={isLoading} />,
      }
    : {
        notifications: <NotificationComponent loading={isLoading} />,
        'privacy-settings': <PrivacyComponent loading={isLoading} />,
        'account-settings': <AccountComponent loading={isLoading} />,
        'verify-contact-details': (
          <VerifyContactComponent loading={isLoading} />
        ),
        'security-login': <SecurityComponent loading={isLoading} />,
        others: <OtherComponent loading={isLoading} />,
      };

  const isGoogleRegistered = !hirist && registerBy === 2;

  let menuItems: {label: string; key: string; phoneLabel: string}[] = hirist
    ? [
        {
          label: 'Email & Push Notification',
          key: 'emailnotification',
          phoneLabel: 'Email & Push Notifications',
        },
        {
          label: 'Privacy',
          key: 'privacy',
          phoneLabel: 'Privacy Settings',
        },
        {
          label: 'Account',
          key: 'account',
          phoneLabel: 'Account Settings',
        },
        {
          label: 'Change Password',
          key: 'changepassword',
          phoneLabel: 'Change your password',
        },
        {
          label: 'Verify Contact Details',
          key: 'verify-contact-details',
          phoneLabel: 'Verify Contact Details',
        },
        {
          label: 'Job & Other Settings',
          key: 'other',
          phoneLabel: 'Job & Other Settings',
        },
      ]
    : [
        {
          label: 'Notifications',
          key: 'notifications',
          phoneLabel: 'Notifications',
        },
        {
          label: 'Privacy Settings',
          key: 'privacy-settings',
          phoneLabel: 'Privacy Settings',
        },
        {
          label: 'Account Settings',
          key: 'account-settings',
          phoneLabel: 'Account Settings',
        },
        {
          label: 'Security & Login',
          key: 'security-login',
          phoneLabel: 'Security & Login',
        },
        {
          label: 'Verify Contact Details',
          key: 'verify-contact-details',
          phoneLabel: 'Verify Contact Details',
        },
        {label: 'Others', key: 'others', phoneLabel: 'Others'},
      ];

  // Hide Security & Login for Google-registered users
  if (isGoogleRegistered) {
    menuItems = menuItems.filter(item => item.key !== 'security-login');
  }

  // If currently selected is hidden, redirect to a safe default
  useEffect(() => {
    if (isGoogleRegistered && selectedItem === 'security-login') {
      setSelectedItem('notifications');
      router.replace(
        {pathname: router.pathname, query: {section: 'notifications'}},
        undefined,
        {shallow: true}
      );
    }
  }, [isGoogleRegistered, selectedItem]);

  const titleMap = menuItems.reduce(
    (acc, item) => {
      acc[item.key] = item.phoneLabel;
      return acc;
    },
    {} as Record<string, string>
  );

  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} />
        <NavLayout
          HeaderComp={
            isSmall && selectedItem && !hirist
              ? () => (
                  <Box position={'absolute'} sx={{width: '100%'}}>
                    <BackButtonHeader
                      title={
                        titleMap[selectedItem] || selectedItem.replace('-', ' ')
                      }
                      onBackClick={() => router.back()}
                      titleExtension={selectedItem === 'verify-contact-details'}
                    />
                  </Box>
                )
              : undefined
          }
        >
          <ResponsiveLayout
            breadcrumbElements={breadcrumbElements}
            variant={VersionVariants.VERSION_TWO}
          >
            {hirist ? (
              <Box mt={{xxs: -1, md: 0}}>
                <HiristBanner>
                  <BannerInfo
                    Icon={Settings}
                    isMobile={isMobile}
                    title={'My Settings'}
                    subTitle={`Change Notifications, Privacy setting, Account settings,
                        Password and Deactivate your account.`}
                  />
                </HiristBanner>
                <Box
                  display={'flex'}
                  justifyContent={'flex-start'}
                  mt={{xxs: 0, md: 6.25}}
                >
                  <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
                  <Grid
                    container
                    sx={{justifyContent: 'flex-start'}}
                    ml={{xxs: 0, md: 2, lg: 4}}
                  >
                    {isSmall ? (
                      <MobileNavbar
                        menuItems={menuItems}
                        selectedKey={selectedItem}
                        onSelect={onSelect}
                      />
                    ) : (
                      <Grid item xs maxWidth={{md: 'fit-content'}}>
                        <NavigationPanelHirist
                          title="My Settings"
                          menu={menuItems}
                          selectedItem={selectedItem}
                          onSelect={onSelect}
                        />
                      </Grid>
                    )}
                    {!!selectedItem && (
                      <Box
                        sx={{
                          mt: {xxs: 3, md: 0},
                          maxWidth: {lg: '78%', md: 715, xs: '100%'},
                          width: '100%',
                        }}
                        display={'flex'}
                        flexDirection={'column'}
                      >
                        {isSmall && (
                          <Typography mb={1.5} px={1.25}>
                            {titleMap[selectedItem] ||
                              selectedItem.replace('-', '')}
                          </Typography>
                        )}
                        <Grid item md>
                          {componentMap[selectedItem] || (
                            <HiristNotificationSetting loading={isLoading} />
                          )}
                        </Grid>
                      </Box>
                    )}
                  </Grid>
                </Box>
              </Box>
            ) : (
              <Box display={'flex'} justifyContent={'flex-start'}>
                <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
                <Grid container>
                  {!!showNavPanel && (
                    <Grid item xs maxWidth={{md: 'fit-content'}}>
                      <NavigationPanel
                        title="My Settings"
                        menu={menuItems}
                        selectedItem={selectedItem}
                        onSelect={onSelect}
                      />
                    </Grid>
                  )}
                  {!!selectedItem && (
                    <Grid
                      item
                      md
                      sx={{maxWidth: {md: 806, xs: '100%'}, width: '100%'}}
                    >
                      {componentMap[selectedItem] || (
                        <NotificationComponent loading={isLoading} />
                      )}
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const section = context.query?.section?.toLowerCase() || null;

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    try {
      let headers = {};

      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);
        console.log(
          '🚀 ~ file: index.tsx:40 ~ store=> ~ cookies:',
          context.req?.headers
        );
        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          headers = {
            cookie: context.req?.headers?.cookie,
            'user-agent': context.req?.headers?.['user-agent'],
          };
          try {
            const {token} = context.query;
            if (token) {
              const result = await userService.verifyContactDetails(
                {verificationToken: token},
                'email',
                headers
              );
              console.log({token, result});
            }
          } catch (error) {
            console.log('verification email api failure', error);
          }
          const proRes = await userService.checkPro('checkpro', {headers});
          console.log('🚀 ~ store=> ~ proRes:', proRes);

          if (proRes?.type && ['refresh', 'logout'].includes(proRes.type)) {
            if (proRes?.type === 'refresh' && proRes?.token) {
              const {ssrLastActCookie, ssrTokenCookie} = setSSRCookies(
                proRes.token
              );

              context.res?.setHeader('Set-Cookie', [
                ssrTokenCookie,
                ssrLastActCookie,
              ]);
            }
            if (proRes?.type === 'logout') {
              const cookiesTodelete = getCookiesToDelete();
              context.res?.setHeader('Set-Cookie', cookiesTodelete);
            }
            throw new Error("User isn't logged in");
          }

          if (proRes?.pro) {
            store.dispatch({type: 'Auth/set_pro', payload: proRes?.pro});
          }
        } else {
          throw new Error("User isn't logged in");
        }
      }
    } catch (error: /* eslint-disable @typescript-eslint/no-explicit-any */ any) {
      return {
        redirect: {
          permanent: false,
          destination: `/`,
        },
      };
    }

    const seoConfig = await loadSEO(
      SETTINGS,
      {
        title: '',
        description: '',
      },
      '',
      true
    );

    return {
      props: {
        isMobile,
        activeSection: section,
        seoConfig,
      },
    };
  }
);

export default MySettings;
